import {
  TreeItem,
  TreeItemType,
} from "@redotech/arbiter/navigation/folder-tree/types";

interface NavActionNames {
  views: ReadonlyMap<string, string>;
  customView: string;
  folder: string;
}

const actionNames = {
  returns: {
    views: new Map([
      ["needs-review", "returns_nav:return_manual_review_view_link_click"],
      [
        "partially-processed",
        "returns_nav:return_partially_processed_view_link_click",
      ],
      [
        "failed-rma-creation",
        "returns_nav:return_failed_rma_creation_view_link_click",
      ],
      [
        "label-creation-failed",
        "returns_nav:return_label_creation_failed_view_link_click",
      ],
      [
        "refund-payment-failed",
        "returns_nav:return_refund_payment_failed_view_link_click",
      ],
      ["open", "returns_nav:return_open_view_link_click"],
      ["in-transit", "returns_nav:return_in_transit_view_link_click"],
      ["flagged", "returns_nav:return_flagged_view_link_click"],
      ["fraud-risk", "returns_nav:return_fraud_risk_view_link_click"],
      [
        "outstanding-balance",
        "returns_nav:return_outstanding_balance_view_link_click",
      ],
      [
        "stuck-in-transit",
        "returns_nav:return_stuck_in_transit_view_link_click",
      ],
      [
        "awaiting-processing",
        "returns_nav:return_awaiting_processing_view_link_click",
      ],
      ["awaiting-remit", "returns_nav:return_awaiting_remit_view_link_click"],
      ["restock-failed", "returns_nav:return_restock_failed_view_link_click"],
      ["out-of-stock", "returns_nav:return_out_of_stock_view_link_click"],
      [
        "tracking-exception",
        "returns_nav:return_tracking_exception_view_link_click",
      ],
      [
        "delayed-in-customs",
        "returns_nav:return_delayed_in_customs_view_link_click",
      ],
      [
        "missing-customs-info",
        "returns_nav:return_missing_customs_info_view_link_click",
      ],
      ["delivered", "returns_nav:return_delivered_view_link_click"],
      ["complete", "returns_nav:return_complete_view_link_click"],
      ["rejected", "returns_nav:return_rejected_view_link_click"],
      ["reset", "returns_nav:return_reset_view_link_click"],
      ["all-returns", "returns_nav:all_returns_link_click"],
    ]),
    customView: "returns_nav:custom_return_view_link_click",
    folder: "returns_nav:return_folder_toggle_click",
  },
  claims: {
    views: new Map([
      ["needs-review", "returns_nav:claim_manual_review_view_link_click"],
      [
        "partially-processed",
        "returns_nav:claim_partially_processed_view_link_click",
      ],
      [
        "failed-rma-creation",
        "returns_nav:claim_failed_rma_creation_view_link_click",
      ],
      [
        "label-creation-failed",
        "returns_nav:claim_label_creation_failed_view_link_click",
      ],
      [
        "refund-payment-failed",
        "returns_nav:claim_refund_payment_failed_view_link_click",
      ],
      ["merchant-review", "returns_nav:claim_merchant_review_view_link_click"],
      [
        "tracking-exception",
        "returns_nav:claim_tracking_exception_view_link_click",
      ],
      ["all", "returns_nav:all_claims_link_click"],
      ["managed", "returns_nav:managed_claims_link_click"],
    ]),
    customView: "returns_nav:custom_claim_view_link_click",
    folder: "returns_nav:claim_folder_toggle_click",
  },
  warranties: {
    views: new Map([
      ["needs-review", "returns_nav:warranty_manual_review_view_link_click"],
      [
        "partially-processed",
        "returns_nav:warranty_partially_processed_view_link_click",
      ],
      [
        "failed-rma-creation",
        "returns_nav:warranty_failed_rma_creation_view_link_click",
      ],
      [
        "label-creation-failed",
        "returns_nav:warranty_label_creation_failed_view_link_click",
      ],
      [
        "refund-payment-failed",
        "returns_nav:warranty_refund_payment_failed_view_link_click",
      ],
      ["repairs", "returns_nav:warranty_repairs_view_link_click"],
      [
        "tracking-exception",
        "returns_nav:warranty_tracking_exception_view_link_click",
      ],
      ["all-warranties", "returns_nav:all_warranties_link_click"],
    ]),
    customView: "returns_nav:custom_warranty_view_link_click",
    folder: "returns_nav:warranty_folder_toggle_click",
  },
  recycle: {
    views: new Map([
      ["needs-review", "returns_nav:recycle_manual_review_view_link_click"],
      [
        "partially-processed",
        "returns_nav:recycle_partially_processed_view_link_click",
      ],
      [
        "failed-rma-creation",
        "returns_nav:recycle_failed_rma_creation_view_link_click",
      ],
      [
        "label-creation-failed",
        "returns_nav:recycle_label_creation_failed_view_link_click",
      ],
      [
        "refund-payment-failed",
        "returns_nav:recycle_refund_payment_failed_view_link_click",
      ],
      ["all-recycle", "returns_nav:all_recycle_link_click"],
    ]),
    customView: "returns_nav:custom_recycle_view_link_click",
    folder: "returns_nav:recycle_folder_toggle_click",
  },
} satisfies Record<string, NavActionNames>;

/** Only reserved system keys are mapped; merchant titles never enter action names. */
export function nameReturnsNavTree(
  items: TreeItem[],
  domain: keyof typeof actionNames,
): TreeItem[] {
  const names = actionNames[domain];
  return items.map((item) =>
    item.type === TreeItemType.Folder
      ? {
          ...item,
          "data-dd-action-name": names.folder,
          children: nameReturnsNavTree(item.children, domain),
        }
      : {
          ...item,
          "data-dd-action-name":
            item["data-dd-action-name"] ??
            names.views.get(item.name) ??
            names.customView,
        },
  );
}
